import {err, ok, ValidationError} from 'framework';
import type {Result, Validator} from 'framework';
import * as z from 'zod';
import {formatByteSize} from '@/shared/lib/format-byte-size';
import {formatImageMimeTypes} from '@/shared/lib/format-image-mime-types';

/** What one upload destination accepts; values are feature-owned and mirror the server limits. */
export type ImageFileConstraints = {
  readonly acceptedMimeTypes: readonly string[];
  readonly maxBytes: number;
};

/**
 * Guards a user-picked `File` before it reaches the network. The mime check
 * runs first so the more fundamental issue wins when both fail. Messages are
 * user-facing and toasted verbatim by the callers.
 */
export class ImageFileValidator implements Validator<File> {
  readonly standardSchema: z.ZodType<File>;

  constructor(constraints: ImageFileConstraints) {
    this.standardSchema = z
      .file()
      .mime([...constraints.acceptedMimeTypes], {
        error: `Choose a ${formatImageMimeTypes(constraints.acceptedMimeTypes)} image.`,
      })
      .max(constraints.maxBytes, {
        error: `Choose an image smaller than ${formatByteSize(constraints.maxBytes)}.`,
      });
  }

  validate(value: unknown): Result<File, ValidationError> {
    const parsed = this.standardSchema.safeParse(value);
    if (!parsed.success) {
      return err(new ValidationError(parsed.error.issues[0]?.message ?? 'Choose a valid image.'));
    }
    return ok(parsed.data);
  }
}
